import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { columns } from "~/components/citations/citations-columns";
import { DataTable } from "~/components/citations/data-table";
import { useProjectContext } from "~/components/layout/project-context";
import { projectCitationsQueryOptions } from "~/lib/projects/queries";

const citationsSearchSchema = z.object({
  search: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().nonnegative().int().catch(0),
  pageSize: z.coerce.number().int().catch(50),
  hide: z.array(z.string()).optional(),
});

export type CitationsSearch = z.infer<typeof citationsSearchSchema>;

export const Route = createFileRoute("/_auth/projects/$projectId/citations")({
  validateSearch: citationsSearchSchema,
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      projectCitationsQueryOptions(params.projectId),
    );
  },
  component: CitationsPage,
});

function CitationsPage() {
  const { projectId, projectTitle } =
    useProjectContext();
  const { data: citations } = useSuspenseQuery(
    projectCitationsQueryOptions(projectId),
  );
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const updateSearch = (updates: Partial<CitationsSearch>) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...updates,
      }),
      replace: true,
    });
  };

  return (
    <>
      <main className="absolute inset-x-0 bottom-0 top-14 overflow-hidden p-4">
        <div className="h-full">
          <DataTable
            columns={columns}
            data={citations}
            search={search}
            onSearchChange={updateSearch}
          />
        </div>
      </main>
    </>
  );
}
